import { z, type ZodTypeAny } from 'zod'
import { computed, type MaybeRefOrGetter, toValue } from 'vue'
import { type RouteLocationNormalizedLoaded, type Router, useRoute, useRouter } from 'vue-router'
import rison from 'rison'
import { Log } from '@/shared/utils/logging/loggers'

let routerInstance: Router|undefined = undefined
let pendingUpdates: Record<string, MaybeRefOrGetter> = { }
let timeoutId: ReturnType<typeof setTimeout>|undefined = undefined

export function useQueryParameter<
  TSchema extends ZodTypeAny = ZodTypeAny,
  TFallback extends z.infer<TSchema> | undefined = undefined
>(
  name: string,
  options?: { schema?: TSchema, fallback?: MaybeRefOrGetter<TFallback> }
) {
  const router = useRouter()
  const route = useRoute()
  const schema = options?.schema ?? z.any()
  const fallback = options?.fallback

  if (!routerInstance) {
    routerInstance = router
  }

  return computed({
    get() {
      const value = getRawValueFromQuery(route, name)

      if (value === undefined) {
        return toValue(fallback)
      }

      try {
        const decoded = rison.decode(value)
        const result = schema.safeParse(decoded)

        if (result.success) {
          return result.data
        }

        Log.debug(
          `Can't marshal the raw value to the correct type (reason: ${result.error.message}). Defaulting to fallback value.`,
          { error: result.error }
        )

        return toValue(fallback)
      } catch (e: unknown) {
        Log.debug(
          `Can't marshal the raw value to the correct type (reason: ${e}). Defaulting to fallback value.`,
          { error: e }
        )

        return toValue(fallback)
      }
    },
    set(newValue) {
      if (newValue === undefined) {
        queueQueryUpdate(name, undefined)
        return
      }

      const result = schema.safeParse(newValue)

      queueQueryUpdate(name, result.success ? rison.encode(result.data) : undefined)
    }
  })
}

function getRawValueFromQuery(route: RouteLocationNormalizedLoaded, name: string) {
  if (!(name in route.query)) {
    return undefined
  }

  const possibleValues = route.query[name]
  const firstValue = Array.isArray(possibleValues) ? possibleValues[0] : possibleValues

  return firstValue ?? undefined
}


/**
 * Add the given key/value pair to the queue of updates to the query string.
 *
 */
function queueQueryUpdate(key: string, value: MaybeRefOrGetter) {
  pendingUpdates = { ...pendingUpdates, [key]: value }

  if (timeoutId) {
    clearTimeout(timeoutId)
  }

  timeoutId = setTimeout(flushQueryUpdates, 0)
}


/**
 * Handles the flushing of the updates to the query string. Used to batch multiple updates in the same
 * tick of the event loop without introducing additional renders.
 *
 */
function flushQueryUpdates() {
  if (!routerInstance) {
    return
  }

  const query = { ...routerInstance.currentRoute.value.query }

  Object.entries(pendingUpdates).forEach(
    ([key, value]) => {
      value = toValue(value)

      if (value === undefined || value === null) {
        delete query[key]
      } else {
        query[key] = value
      }
    }
  )

  routerInstance.replace({ query })
  pendingUpdates = { }
  timeoutId = undefined
}
